import moment from 'moment';
import { useFormatMessage, useFormatAddress } from 'hooks';
import CONSTANTS from 'utils/constants';
import * as yup from 'yup';
import { getFormatMessage } from 'utils/schema';

const i18nEntityName = 'Station';

export const yupSchema = yup.object().shape({
  name: yup.string(),
  status: yup.string(),
  address: yup.object().shape({
    line1: yup.string(),
    line2: yup.string(),
    postalCode: yup.string(),
    city: yup.string(),
    country: yup.string(),
  }),
  location: yup
    .object()
    .shape({
      latitude: yup
        .number()
        .transform((value) => (Number.isNaN(value) ? undefined : value)),
      longitude: yup
        .number()
        .transform((value) => (Number.isNaN(value) ? undefined : value)),
    })
    .notRequired(),
  BMSVersion: yup.string(),
  CPUVersion: yup.string(),
  hardwareVersion: yup.string(),
  IMEI: yup.string(),
  phoneNumber: yup.string(),
  LNBTilt: yup
    .number()
    .transform((value) => (Number.isNaN(value) ? undefined : value)),
  elevation: yup
    .number()
    .transform((value) => (Number.isNaN(value) ? undefined : value)),
  azimut: yup
    .number()
    .transform((value) => (Number.isNaN(value) ? undefined : value)),
  satelliteId: yup.string(),
  antennaSize: yup.string(),
  installationNotes: yup.string(),
  darkeningSNRThreshold: yup
    .number()
    .transform((value) => (Number.isNaN(value) ? undefined : value)),
  darkeningSNR: yup
    .number()
    .transform((value) => (Number.isNaN(value) ? undefined : value)),
  darkeningStartedAt: yup.string(),
  darkeningStoppedAt: yup.string(),
  darkeningSignal: yup.string().default(CONSTANTS.SIGNALS[6]),
  serialNumber: yup.lazy((item) =>
    typeof item === 'number'
      ? yup
          .number()
          .transform((value) => (Number.isNaN(value) ? undefined : value))
      : yup.string()
  ),
  isWLHActive: yup.boolean(),
  isNLHActive: yup.boolean(),
  isWLVActive: yup.boolean(),
  isNLVActive: yup.boolean(),
  isWUHActive: yup.boolean(),
  isNUHActive: yup.boolean(),
  isWUVActive: yup.boolean(),
  isNUVActive: yup.boolean(),
  contact: yup.object().shape({
    name: yup.string(),
    phoneNumber: yup.string(),
  }),
  createdAt: yup.string(),
  updatedAt: yup.string(),
});

export const infoSchemas = [
  {
    key: 'name',
    label: getFormatMessage,
    _form: {
      label: () => {
        return useFormatMessage(`${i18nEntityName}.name`);
      },
      id: 'name',
      // required: true, // for yup
      name: 'name',
      type: 'text',
    },
  },
  {
    key: 'serialNumber',
    label: getFormatMessage,

    _form: {
      label: () => {
        return useFormatMessage(`${i18nEntityName}.serialNumber`);
      },
      id: 'serialNumber',
      name: 'serialNumber',
      type: 'text',
    },
  },
  {
    key: 'address',
    label: (_entity, key, options) => {
      const { userData = {} } = options;
      const isStandardUser = CONSTANTS.IS_USER_ROLE_STANDARD[userData.role];

      if (isStandardUser) {
        return null;
      }
      return getFormatMessage(_entity, key, options);
    },
    contentTitle: (_entity, key, options) => {
      const { userData = {} } = options;
      const isStandardUser = CONSTANTS.IS_USER_ROLE_STANDARD[userData.role];

      if (!_entity || !_entity.location || isStandardUser) {
        return '';
      }
      return `lat:${_entity.location.latitude}, lng:${_entity.location.longitude}`;
    },
    content: (_entity) => {
      return useFormatAddress(_entity.address);
    },
    _form: {
      type: 'object',
      properties: {
        line1: {
          id: 'address.line1',
          name: 'address.line1',
          type: 'text',
          label: (_entity, key, options) => {
            return getFormatMessage(_entity, 'address.line1', options);
            // return useFormatMessage();
          },
        },
        line2: {
          id: 'address.line2',
          name: 'address.line2',
          type: 'text',
          label: () => {
            return useFormatMessage(`${i18nEntityName}.address.line2`);
          },
        },
        postalCode: {
          id: 'address.postalCode',
          name: 'address.postalCode',
          type: 'text',
          label: () => {
            return useFormatMessage(`${i18nEntityName}.address.postalCode`);
          },
        },
        city: {
          id: 'address.city',
          name: 'address.city',
          type: 'text',
          label: () => {
            return useFormatMessage(`${i18nEntityName}.address.city`);
          },
        },
        country: {
          id: 'address.country',
          name: 'address.country',
          type: 'text',
          label: () => {
            return useFormatMessage(`${i18nEntityName}.address.country`);
          },
        },
      },
    },
  },
  {
    key: 'location',
    _form: {
      type: 'object',
      properties: {
        latitude: {
          id: 'location.latitude',
          name: 'location.latitude',
          type: 'number',
          options: {
            step: '0.00000000001',
          },
          label: () => {
            return useFormatMessage(`${i18nEntityName}.location.latitude`);
          },
        },
        longitude: {
          id: 'location.longitude',
          name: 'location.longitude',
          type: 'number',
          options: {
            step: '0.00000000001',
          },
          label: () => {
            return useFormatMessage(`${i18nEntityName}.location.longitude`);
          },
        },
      },
    },
  },
  {
    key: 'status',
    label: getFormatMessage,
    _form: {
      label: getFormatMessage,
      id: 'status',
      name: 'status',
      type: 'select',
      inputs: (_entity, key, options) => {
        const { userData = {} } = options;
        const isInstaller = CONSTANTS.IS_USER_ROLE_INSTALLER[userData.role];
        const statusEnum = isInstaller
          ? CONSTANTS.STATIONS_STATUS_FOR_INSTALLER
          : CONSTANTS.STATIONS_STATUS;
        const keyOptions = statusEnum.map((status) => {
          return { value: status, label: status };
        });

        return keyOptions || [];
      },
    },
  },
];

export const groupsSchemas = [
  {
    key: 'stationsUsersGroups',
    _form: {
      label: getFormatMessage,

      id: 'stationsUsersGroups',
      name: 'stationsUsersGroups',
      type: 'fieldset',
      /**
       *
       * @param {*} _entity
       * @param {*} key
       * @param {} options
       * [{ value: 'qO7TLflbsNRmqtPNmcO7tRZ16lp1', label: 'M' }]
       * @returns
       */
      inputs: (_entity, key, options) => {
        if (key && options) {
          const values = options[key];

          const valuesOptions = values.map((group) => {
            const stationId = _entity.id;
            let checked = false;
            if (
              group &&
              group.stationsIds &&
              group.stationsIds.includes(stationId)
            ) {
              checked = true;
            }
            return { label: group.name, value: group.id, checked };
          });
          if (valuesOptions) {
            return valuesOptions;
          }
        }
        return [];
      },
    },
  },
];

export const contactSchemas = [
  {
    key: 'contact',
    label: getFormatMessage,

    content: (_entity) => {
      if (!_entity.contact) {
        return 'N/A';
      }
      return [_entity.contact.name, _entity.contact.phoneNumber].join(' - ');
    },
    _form: {
      type: 'object',
      properties: {
        name: {
          id: 'contact.name',
          name: 'contact.name',
          type: 'text',
          label: () => {
            return useFormatMessage(`${i18nEntityName}.name`);
          },
        },
        phoneNumber: {
          id: 'contact.phoneNumber',
          name: 'contact.phoneNumber',
          type: 'text',
          label: () => {
            return useFormatMessage(`${i18nEntityName}.phoneNumber`);
          },
        },
      },
    },
  },
];

export const statusSchema = [
  {
    key: 'UTCTime',
    label: () => {
      return useFormatMessage(`${i18nEntityName}.UTCTime`);
    },
    content: () => {
      return moment.utc().format('DD/MM - HH[h]mm:ss');
    },
  },
  {
    key: 'healthDataSignal',
    label: getFormatMessage,
    content: (_entity, key, options) => {
      const { lastStatus } = options;
      let signalHealth = 0;
      let lastStatusStr = '';
      if (lastStatus && lastStatus.signal) {
        const lastSignalStatus = lastStatus.signal;
        signalHealth = lastSignalStatus.health;
        lastStatusStr = `${signalHealth * 100}%`;
      }
      return lastStatusStr;
    },
  },
  {
    key: 'lastDataSignal',
    label: getFormatMessage,
    content: (_entity, key, options) => {
      const { lastStatus } = options;
      let lastSignalEpoch = null;
      let lastSignalEpochStr = '';
      let lastStatusStr = '';
      let content = '';
      if (lastStatus && lastStatus.signal) {
        const lastSignalStatus = lastStatus.signal;
        lastStatusStr = lastSignalStatus.status;
        lastSignalEpoch = lastSignalStatus.epoch;
        if (lastSignalEpoch) {
          lastSignalEpochStr = moment
            .utc(lastSignalEpoch)
            .format('DD/MM - HH[h]mm');
        }
        content = `${lastStatusStr} - ${lastSignalEpochStr} UTC`;
      }
      return content;
    },
  },
  {
    key: 'batteryVoltage',
    label: getFormatMessage,
    content: (_entity, key, options) => {
      const { lastStatus } = options;
      let lastEpoch = null;
      let lastEpochStr = 'N/A';
      let lastValue = null;
      let lastValueStr = 'N/A';
      let lastStatusStr = 'N/A';
      let content = '';
      if (lastStatus && lastStatus.status) {
        const lastBatteryStatus = lastStatus.status;
        lastStatusStr = lastBatteryStatus.status;

        lastEpoch = lastBatteryStatus.epoch;
        if (lastEpoch) {
          lastEpochStr = moment.utc(lastEpoch).format('DD/MM - HH[h]mm');
          lastValue = lastBatteryStatus.value;
          lastValueStr = `${lastValue}V`;
        }
        content = `${lastStatusStr} - ${lastValueStr} - ${lastEpochStr} UTC`;
      }

      return content;
    },
  },

  {
    key: 'LNBTemperature',
    label: getFormatMessage,
    content: (_entity, key, options) => {
      const { lastStatus } = options;
      let lastEpoch = null;
      let lastEpochStr = 'N/A';
      let lastValue = null;
      let lastValueStr = 'N/A';
      let lastStatusValue = null;
      let lastStatusStr = 'N/A';

      let content = '';
      if (lastStatus && lastStatus.locales) {
        const lastLocalesStatus = lastStatus.locales;
        lastEpoch = lastLocalesStatus.epoch;
        lastStatusValue = lastLocalesStatus.status;
        lastStatusStr = lastStatusValue;
        if (lastEpoch) {
          lastEpochStr = moment.utc(lastEpoch).format('DD/MM - HH[h]mm');
          lastValue = lastLocalesStatus.value;
          lastValueStr = `${lastValue}°C`;
        }

        content = `${lastStatusStr} - ${lastValueStr} - ${lastEpochStr} UTC`;
      }

      return content;
    },
  },
  {
    key: 'TPHSensor',
    label: getFormatMessage,
    content: (_entity, key, options) => {
      const { lastStatus } = options;
      let lastEpoch = null;
      let lastEpochStr = '';
      let lastTemperatureValue = null;
      let lastHumidityValue = null;
      let lastPressureValue = null;
      let lastTemperatureStr = '';
      let lastHumidityStr = '';
      let lastPressureStr = '';
      let content = '';
      if (lastStatus && lastStatus.locales && lastStatus.locales.temperature) {
        const lastLocalesTemperature = lastStatus.locales.temperature;

        lastEpoch = lastLocalesTemperature.epoch;
        lastEpochStr = moment.utc(lastEpoch).format('DD/MM - HH[h]mm');
        lastTemperatureValue = lastLocalesTemperature.value;
        lastTemperatureStr = `${lastTemperatureValue}°C`;
        content = lastTemperatureStr;
      }

      if (lastStatus && lastStatus.locales && lastStatus.locales.humidity) {
        const lastLocalesField = lastStatus.locales.humidity;

        lastEpoch = lastLocalesField.epoch;
        lastEpochStr = moment.utc(lastEpoch).format('DD/MM - HH[h]mm');
        lastHumidityValue = lastLocalesField.value;
        lastHumidityStr = `${lastHumidityValue}%`;
        content = [content, lastHumidityStr].join(' - ');
      }

      if (lastStatus && lastStatus.locales && lastStatus.locales.pressure) {
        const lastLocalesField = lastStatus.locales.pressure;

        lastEpoch = lastLocalesField.epoch;
        lastEpochStr = moment.utc(lastEpoch).format('DD/MM - HH[h]mm');
        lastPressureValue = lastLocalesField.value;
        lastPressureStr = `${lastPressureValue}`;
        content = [content, lastPressureStr].join(' - ');
      }
      if (lastEpoch) {
        content = [content, lastEpochStr].join(' - ');
      }

      return content;
    },
  },
];

export const versionSchemas = [
  {
    key: 'BMSVersion',
    label: getFormatMessage,
    _form: {
      label: () => {
        return useFormatMessage(`${i18nEntityName}.BMSVersion`);
      },
      id: 'BMSVersion',
      name: 'BMSVersion',
      type: 'text',
    },
  },
  {
    key: 'CPUVersion',
    label: getFormatMessage,
    _form: {
      label: () => {
        return useFormatMessage(`${i18nEntityName}.CPUVersion`);
      },
      id: 'CPUVersion',
      name: 'CPUVersion',
      type: 'text',
    },
  },
  {
    key: 'hardwareVersion',
    label: getFormatMessage,
    _form: {
      label: () => {
        return useFormatMessage(`${i18nEntityName}.hardwareVersion`);
      },
      id: 'hardwareVersion',
      name: 'hardwareVersion',
      type: 'text',
    },
  },
];

export const satelliteSchemas = [
  {
    key: 'satelliteId',
    label: getFormatMessage,
    content: (_entity, key, options = {}) => {
      const satelliteId = _entity[key];

      const { satellites = [] } = options;
      let satellite = null;
      for (let i = 0; i < satellites.length; i += 1) {
        const value = satellites[i];
        const { id } = value;
        if (satelliteId === id) {
          satellite = value;
          break;
        }
      }
      let content = 'N/A';
      if (satellite) {
        content = satellite.name;
      }
      return content;
    },
    _form: {
      label: (_entity, key, options) => {
        return getFormatMessage(_entity, 'satelliteId', options);
      },
      id: 'satelliteId',
      name: 'satelliteId',
      type: 'select',
      inputs: (_entity, key, options = {}) => {
        const { satellites = [] } = options;

        const keyOptions = satellites.map((satellite) => {
          const { id, name } = satellite;
          return { value: id, label: name };
        });

        return [{ value: '', label: '' }, ...keyOptions] || [];
      },
    },
  },
  {
    key: 'azimut',
    label: getFormatMessage,
    _form: {
      label: (_entity, key, options) => {
        return getFormatMessage(_entity, 'azimut', options);
      },
      //   label: () => {
      //   return useFormatMessage(`${i18nEntityName}.azimut`);
      // },
      id: 'azimut',
      name: 'azimut',
      type: 'text',
      options: {
        readOnly: true,
      },
    },
  },
  {
    key: 'elevation',
    label: getFormatMessage,
    _form: {
      label: (_entity, key, options) => {
        return getFormatMessage(_entity, 'elevation', options);
      },
      //    label: () => {
      //   return useFormatMessage(`${i18nEntityName}.elevation`);
      // },
      id: 'elevation',
      name: 'elevation',
      type: 'text',
      options: {
        readOnly: true, // Seuls les champs textuels sont en readonly : https://developer.mozilla.org/fr/docs/Web/HTML/Element/Input
      },
    },
  },
  {
    key: 'LNBTilt',
    label: getFormatMessage,
    _form: {
      label: (_entity, key, options) => {
        return getFormatMessage(_entity, 'LNBTilt', options);
      },
      //   label: () => {
      //   return useFormatMessage(`${i18nEntityName}.LNBTilt`);
      // },
      id: 'LNBTilt',
      name: 'LNBTilt',
      type: 'number',
      options: {
        step: '0.00000001',
      },
    },
  },
  {
    key: 'antennaSize',
    label: getFormatMessage,
    _form: {
      label: (_entity, key, options) => {
        return getFormatMessage(_entity, 'antennaSize', options);
      },

      // label: () => {
      //   return useFormatMessage(`${i18nEntityName}.antennaSize`);
      // },
      id: 'antennaSize',
      name: 'antennaSize',
      type: 'number',
      options: {
        step: '1',
      },
    },
  },
  {
    key: 'installationNotes',
    label: getFormatMessage,
    _form: {
      label: getFormatMessage,
      id: 'installationNotes',
      name: 'installationNotes',
      type: 'text',
    },
  },
  {
    key: 'darkeningSNRThreshold',
    label: getFormatMessage,
    _form: {
      label: (_entity, key, options) => {
        return getFormatMessage(_entity, 'darkeningSNRThreshold', options);
      },
      //  label: () => {
      //   return useFormatMessage(`${i18nEntityName}.darkeningSNR`);
      // },
      id: 'darkeningSNRThreshold',
      name: 'darkeningSNRThreshold',
      type: 'number',
      options: {
        step: '0.01',
      },
    },
  },
  {
    key: 'darkeningSignal',
    label: getFormatMessage,
    _form: {
      label: (_entity, key, options) => {
        return getFormatMessage(_entity, 'darkeningSignal', options);
      },
      id: 'darkeningSignal',
      name: 'darkeningSignal',
      type: 'select',
      inputs: () => {
        const keyOptions = CONSTANTS.SIGNALS.map((signal) => {
          return { value: signal, label: signal };
        });
        return keyOptions || [];
      },
    },
  },
  {
    key: 'darkeningSNR',
    label: getFormatMessage,
    _form: {
      label: (_entity, key, options) => {
        return getFormatMessage(_entity, 'darkeningSNR', options);
      },
      //  label: () => {
      //   return useFormatMessage(`${i18nEntityName}.darkeningSNR`);
      // },
      id: 'darkeningSNR',
      name: 'darkeningSNR',
      type: 'number',
      options: {
        step: '0.01',
      },
    },
  },
  {
    key: 'darkeningStartedAt',
    label: getFormatMessage,
    _form: {
      label: (_entity, key, options) => {
        return getFormatMessage(_entity, 'darkeningStartedAt', options);
      },
      id: 'darkeningStartedAt',
      name: 'darkeningStartedAt',
      type: 'string',
    },
  },
  {
    key: 'darkeningStoppedAt',
    label: getFormatMessage,
    _form: {
      label: (_entity, key, options) => {
        return getFormatMessage(_entity, 'darkeningStoppedAt', options);
      },
      id: 'darkeningStoppedAt',
      name: 'darkeningStoppedAt',
      type: 'string',
    },
  },
];

export const getSatelliteKeys = () => {
  const keys = satelliteSchemas.map((schema) => {
    return schema.key;
  });
  return keys;
};

export const phoneSchemas = [
  {
    key: 'IMEI',
    label: getFormatMessage,
    _form: {
      label: () => {
        return useFormatMessage(`${i18nEntityName}.IMEI`);
      },
      id: 'IMEI',
      name: 'IMEI',
      type: 'text',
    },
  },
  {
    key: 'phoneNumber',
    label: getFormatMessage,
    _form: {
      label: () => {
        return useFormatMessage(`${i18nEntityName}.phoneNumber`);
      },
      id: 'phoneNumber',
      name: 'phoneNumber',
      type: 'text',
    },
  },
];

export const measuresSchemas = CONSTANTS.STATION_KEYS.MEASURES.map(
  (measureKey) => {
    const measureSchema = {
      key: measureKey,
      _form: {
        label: () => {
          return useFormatMessage(`${i18nEntityName}.${measureKey}`);
        },
        id: measureKey,
        name: measureKey,
        type: 'boolean',
      },
    };
    return measureSchema;
  }
);
